import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input, Radio } from 'antd';
import { FC } from 'react';
import {
	isValidCpf,
	maskCpf,
	sanitizeCpf,
} from '../../../../../../../lib/utils/cpfValidator';
import { Nationality } from '../../../dto/SearchTagDto';
import { EditableRow, EMPTY_ROW } from '../types';

interface IProps {
	rows: EditableRow[];
	onChange: (rows: EditableRow[]) => void;
	loading: boolean;
}

export const EditStep: FC<IProps> = ({ rows, onChange, loading }) => {
	const updateRow = (index: number, patch: Partial<EditableRow>) => {
		const next = rows.map((row, i) =>
			i === index ? { ...row, ...patch } : row
		);
		onChange(next);
	};

	const removeRow = (index: number) => {
		onChange(rows.filter((_, i) => i !== index));
	};

	const addRow = () => {
		onChange([...rows, { ...EMPTY_ROW }]);
	};

	return (
		<div>
			<div className='mb-3 text-sm text-gray-500'>
				Revise cada linha antes de validar. Linhas vazias serão descartadas.
			</div>

			<div
				className='overflow-x-auto'
				style={{ maxHeight: 420, overflowY: 'auto' }}
			>
				<table className='w-full text-sm border-collapse'>
					<thead className='bg-gray-50 sticky top-0'>
						<tr>
							<th className='p-2 text-left w-10'>#</th>
							<th className='p-2 text-left w-44'>Nacionalidade *</th>
							<th className='p-2 text-left w-48'>Documento *</th>
							<th className='p-2 text-left'>Nome *</th>
							<th className='p-2 text-left'>Email</th>
							<th className='p-2 text-left w-10'></th>
						</tr>
					</thead>
					<tbody>
						{rows.map((row, index) => {
							const isBR = row.nationality === 'Brasileiro';
							const sanitized = isBR ? sanitizeCpf(row.document) : '';
							const cpfInvalid =
								isBR &&
								sanitized.length > 0 &&
								(sanitized.length !== 11 || !isValidCpf(sanitized));

							return (
								<tr key={index} className='border-b'>
									<td className='p-2 align-top'>{index + 1}</td>
									<td className='p-2 align-top'>
										<Radio.Group
											value={row.nationality || undefined}
											onChange={(e) => {
												const nationality = e.target
													.value as Nationality;
												updateRow(index, {
													nationality,
													document: '',
												});
											}}
											disabled={loading}
										>
											<Radio value='Brasileiro'>BR</Radio>
											<Radio value='Estrangeiro'>EXT</Radio>
										</Radio.Group>
									</td>
									<td className='p-2 align-top'>
										<Input
											value={
												isBR ? maskCpf(row.document) : row.document
											}
											onChange={(e) =>
												updateRow(index, {
													document: isBR
														? maskCpf(e.target.value)
														: e.target.value,
												})
											}
											placeholder={
												isBR ? '000.000.000-00' : 'Documento'
											}
											maxLength={isBR ? 14 : 100}
											status={cpfInvalid ? 'error' : ''}
											disabled={loading}
										/>
										{cpfInvalid && (
											<div className='text-xs text-red-600 mt-1'>
												CPF inválido
											</div>
										)}
									</td>
									<td className='p-2 align-top'>
										<Input
											value={row.title}
											onChange={(e) =>
												updateRow(index, { title: e.target.value })
											}
											disabled={loading}
										/>
									</td>
									<td className='p-2 align-top'>
										<Input
											value={row.email}
											onChange={(e) =>
												updateRow(index, { email: e.target.value })
											}
											placeholder='email@dominio.com'
											disabled={loading}
										/>
									</td>
									<td className='p-2 align-top text-right'>
										<Button
											type='text'
											danger
											icon={<DeleteOutlined />}
											onClick={() => removeRow(index)}
											disabled={loading}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			<div className='mt-3'>
				<Button
					icon={<PlusOutlined />}
					onClick={addRow}
					disabled={loading}
				>
					Adicionar linha
				</Button>
			</div>
		</div>
	);
};
